import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Box, Container, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import FlipIcon from '@mui/icons-material/Flip';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';
import { TEIN_ATU_BADGE_FILE } from '../../utils/idCardTemplate';

// ISO CR80: 85.6mm × 53.98mm ≈ 324×204px @ 96dpi (aspect ≈ 1.586); corner radius 3.18mm ≈ 12px
const CARD_WIDTH = 324;
const CARD_HEIGHT = Math.round((CARD_WIDTH * 53.98) / 85.6); // 204 — keeps exact CR80 ratio
const CARD_RADIUS = 12;
const NDC_GREEN = '#006B3F';
const NDC_RED = '#CE1126';
const PURE_BLACK = '#000000';
const PURE_WHITE = '#FFFFFF';
const TEXT_GRAY = '#333333';

/** Path under `public/` (honours CRA `PUBLIC_URL` for subpath deploys) */
function publicAsset(filename) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return base ? `${base}/${filename}` : `/${filename}`;
}

// Format expiry from school_completion_date or default to Dec of current year
function formatExpiry(schoolCompletionDate) {
  if (schoolCompletionDate) {
    const d = new Date(schoolCompletionDate);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  }
  const now = new Date();
  return `DEC ${now.getFullYear()}`;
}

/** NDC brand initials — deep red top-left → deep green bottom-right */
function GeometricBackground() {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, ${NDC_RED} 0%, ${NDC_GREEN} 100%)`,
        }}
      />
      {/* Low-poly shards for depth */}
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${CARD_WIDTH} ${CARD_HEIGHT}`}
        preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden
      >
        <polygon points="0,0 140,0 90,95" fill={PURE_BLACK} fillOpacity="0.14" />
        <polygon points={`${CARD_WIDTH},0 190,0 255,70`} fill={PURE_WHITE} fillOpacity="0.11" />
        <polygon points={`${CARD_WIDTH},${CARD_HEIGHT} 170,${CARD_HEIGHT} 215,130`} fill={PURE_BLACK} fillOpacity="0.12" />
        <polygon points={`0,${CARD_HEIGHT} 110,${CARD_HEIGHT} 55,150`} fill={PURE_WHITE} fillOpacity="0.07" />
        <polygon points="150,40 300,20 270,110" fill={PURE_BLACK} fillOpacity="0.09" />
        <polygon points={`60,130 210,160 130,${CARD_HEIGHT}`} fill={PURE_WHITE} fillOpacity="0.06" />
        <polygon points="0,80 70,0 100,90" fill={PURE_BLACK} fillOpacity="0.08" />
        <polygon points={`240,80 ${CARD_WIDTH},40 ${CARD_WIDTH},120`} fill={PURE_WHITE} fillOpacity="0.05" />
      </svg>
    </Box>
  );
}

function memberInitials(member) {
  const a = (member?.first_name?.[0] || '').trim();
  const b = (member?.last_name?.[0] || '').trim();
  const two = `${a}${b}`.toUpperCase();
  if (two) return two;
  return (a || member?.first_name?.[0] || 'M').toUpperCase();
}

/** NDC umbrella: white ring, contain in circle (vector). */
function BrandLogoDisc({ src, alt, size = 38, bg = PURE_WHITE }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `3px solid ${PURE_WHITE}`,
        bgcolor: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        p: '3px',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </Box>
  );
}

/**
 * Chapter photo (e.g. TEIN ATU): square raster with circular artwork — crop to a true disc
 * (object-fit cover) so a rectangular mat is not visible inside a round frame.
 */
function ChapterRoundDisc({ src, alt, size = 44, borderWidth = 2 }) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
        border: `${borderWidth}px solid ${PURE_WHITE}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        bgcolor: 'transparent',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
        }}
      />
    </Box>
  );
}

export default function IdCard() {
  const [data, setData] = useState(null);
  const [approved, setApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flipped, setFlipped] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }
    fetchCard();
  }, [navigate]);

  const fetchCard = async () => {
    try {
      setLoading(true);
      setError('');
      const api = (await import('../../utils/api')).default;
      const token = localStorage.getItem('memberToken');
      if (token) api.defaults.headers.common['x-auth-token'] = token;
      const res = await api.get('/member-portal/card');
      setApproved(res.data.approved === true);
      setData(res.data.data || null);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('memberToken');
        navigate('/member-portal/login');
      } else {
        setError(err.response?.data?.message || 'Failed to load card');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!approved || !data?.member) {
    return (
      <Box>
        <Container maxWidth="sm" sx={{ mb: 4 }}>
          <Alert severity="info">
            Your digital ID card will appear here after admin approval. Physical cards are printed externally.
          </Alert>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/member-portal')}>
            Back to Dashboard
          </Button>
        </Container>
      </Box>
    );
  }

  const m = data.member;
  const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ') || 'Member';
  const idNumber = m.member_id || m.association_number || '—';
  const institution = 'Accra Technical University';
  const expiry = formatExpiry(m.school_completion_date);
  const verifyUrl = `${window.location.origin}/verify/${m.id}`;
  const initials = memberInitials(m);

  const cardFontFamily = '"Montserrat", "Roboto", sans-serif';

  return (
    <Box>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Button variant="outlined" startIcon={<FlipIcon />} onClick={() => setFlipped((f) => !f)}>
            {flipped ? 'Show front' : 'Show back'}
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: CARD_WIDTH,
              aspectRatio: `${CARD_WIDTH} / ${CARD_HEIGHT}`,
              position: 'relative',
              perspective: '1000px',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                transformStyle: 'preserve-3d',
                transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                transition: 'transform 0.6s ease',
              }}
            >
            {/* Front - Per spec: CR80, corner radius 3.18mm, low-poly background */}
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                overflow: 'hidden',
                borderRadius: `${CARD_RADIUS}px`,
                border: `1px solid ${PURE_BLACK}`,
                p: 0,
                fontFamily: cardFontFamily,
              }}
            >
              <GeometricBackground />
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  p: 1.5,
                  gap: 0.5,
                }}
              >
                {/* Top: NDC + TEIN ATU chapter mark; title block uses remaining width */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    <BrandLogoDisc
                      src={publicAsset('ndc-logo.svg')}
                      alt="National Democratic Congress"
                      size={40}
                    />
                    <ChapterRoundDisc
                      src={publicAsset(TEIN_ATU_BADGE_FILE)}
                      alt="Accra Technical University"
                      size={40}
                      borderWidth={2}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontWeight: 700,
                        fontSize: '10pt',
                        letterSpacing: 0.4,
                        lineHeight: 1.18,
                        fontFamily: cardFontFamily,
                        textTransform: 'uppercase',
                      }}
                    >
                      TERTIARY EDUCATION INSTITUTIONS NETWORK
                    </Typography>
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontWeight: 400,
                        fontSize: '6.75pt',
                        letterSpacing: 0.22,
                        lineHeight: 1.28,
                        fontFamily: cardFontFamily,
                        textTransform: 'uppercase',
                        mt: 0.2,
                      }}
                    >
                      Membership Card
                    </Typography>
                  </Box>
                </Box>

                {/* Photo + sub-photo ID (left); member fields (right) */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'stretch',
                    flex: '1 1 auto',
                    minHeight: 0,
                    pb: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      alignSelf: 'flex-start',
                      width: 88,
                      maxWidth: '36%',
                    }}
                  >
                    <MemberProfileAvatar
                      member={m}
                      size={72}
                      sx={{
                        border: `4px solid ${PURE_WHITE}`,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: PURE_WHITE,
                      }}
                    />
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontWeight: 800,
                        fontSize: '13pt',
                        lineHeight: 1.1,
                        mt: 0.5,
                        fontFamily: cardFontFamily,
                        letterSpacing: 0.5,
                      }}
                    >
                      {initials}
                    </Typography>
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontWeight: 500,
                        fontSize: '5.8pt',
                        lineHeight: 1.15,
                        mt: 0.2,
                        textAlign: 'center',
                        fontFamily: cardFontFamily,
                        textTransform: 'uppercase',
                        wordBreak: 'break-word',
                      }}
                    >
                      {fullName}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      minHeight: 0,
                      pt: 0.25,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      py: 0.75,
                    }}
                  >
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontSize: '8pt',
                        fontWeight: 700,
                        fontFamily: cardFontFamily,
                        display: 'block',
                        textTransform: 'uppercase',
                        lineHeight: 1.38,
                      }}
                    >
                      NAME: <Box component="span" sx={{ fontWeight: 400 }}>{fullName}</Box>
                    </Typography>
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontSize: '8pt',
                        fontWeight: 700,
                        fontFamily: cardFontFamily,
                        display: 'block',
                        textTransform: 'uppercase',
                        lineHeight: 1.38,
                      }}
                    >
                      INSTITUTION: <Box component="span" sx={{ fontWeight: 400 }}>{institution}</Box>
                    </Typography>
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontSize: '8pt',
                        fontWeight: 700,
                        fontFamily: cardFontFamily,
                        display: 'block',
                        textTransform: 'uppercase',
                        lineHeight: 1.38,
                      }}
                    >
                      MEMBER ID: <Box component="span" sx={{ fontWeight: 400 }}>{idNumber}</Box>
                    </Typography>
                    <Typography
                      sx={{
                        color: PURE_WHITE,
                        fontSize: '8pt',
                        fontWeight: 700,
                        fontFamily: cardFontFamily,
                        display: 'block',
                        textTransform: 'uppercase',
                        lineHeight: 1.38,
                      }}
                    >
                      EXPIRY: <Box component="span" sx={{ fontWeight: 400 }}>{expiry}</Box>
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 'auto',
                    pt: 0.5,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.65,
                    alignItems: 'stretch',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        border: `2px solid ${NDC_RED}`,
                        borderRadius: 0.5,
                        overflow: 'hidden',
                        bgcolor: PURE_WHITE,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 0.25,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      <QRCodeSVG value={verifyUrl} size={44} level="M" includeMargin={false} />
                    </Box>
                  </Box>
                  <Typography
                    sx={{
                      color: PURE_WHITE,
                      textAlign: 'center',
                      fontWeight: 500,
                      fontSize: '6.8pt',
                      letterSpacing: 0.35,
                      fontFamily: cardFontFamily,
                      textTransform: 'uppercase',
                    }}
                  >
                    UNITY, STABILITY, AND DEVELOPMENT
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Back - Per spec: magnetic stripe, barcode, disclaimer, contact, headquarters */}
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                overflow: 'hidden',
                borderRadius: `${CARD_RADIUS}px`,
                border: `1px solid ${PURE_BLACK}`,
                bgcolor: PURE_WHITE,
                p: 0,
                fontFamily: cardFontFamily,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Magnetic stripe */}
              <Box sx={{ height: 32, flexShrink: 0, bgcolor: PURE_BLACK, width: '100%' }} />
              {/* Barcode - Code 128 style */}
              <Box
                sx={{
                  height: 28,
                  flexShrink: 0,
                  mx: 1.5,
                  mt: 0.75,
                  background: `repeating-linear-gradient(90deg, ${PURE_BLACK} 0, ${PURE_BLACK} 1.5px, transparent 1.5px, transparent 3px)`,
                  backgroundSize: '3px 100%',
                }}
              />
              <Box sx={{ px: 1.5, pt: 0.75, pb: 1.25, flex: 1, minHeight: 0 }}>
                <Typography sx={{ fontSize: '5.6pt', color: TEXT_GRAY, fontFamily: 'Arial, Helvetica, sans-serif', mb: 0.4, lineHeight: 1.28 }}>
                  This card remains the property of the NDC TEIN. If found, please return to any NDC office.
                </Typography>
                <Typography sx={{ fontSize: '5.6pt', color: TEXT_GRAY, fontFamily: 'Arial, Helvetica, sans-serif', display: 'block', lineHeight: 1.25 }}>
                  Website: www.ndc.org.gh/tein
                </Typography>
                <Typography sx={{ fontSize: '5.6pt', color: TEXT_GRAY, fontFamily: 'Arial, Helvetica, sans-serif', display: 'block', lineHeight: 1.25 }}>
                  Email: tein@ndc.org.gh
                </Typography>
                <Typography sx={{ fontSize: '5.6pt', color: TEXT_GRAY, fontFamily: 'Arial, Helvetica, sans-serif', display: 'block', lineHeight: 1.25 }}>
                  Phone: +233 55 728 7496
                </Typography>
                <Typography sx={{ fontSize: '5.6pt', color: TEXT_GRAY, fontFamily: 'Arial, Helvetica, sans-serif', mt: 0.5, display: 'block', lineHeight: 1.25 }}>
                  National Headquarters:
                </Typography>
                <Typography sx={{ fontSize: '5.6pt', color: TEXT_GRAY, fontFamily: 'Arial, Helvetica, sans-serif', display: 'block', lineHeight: 1.25 }}>
                  P.O. Box AN 5825, Adabraka, Accra, Ghana
                </Typography>
                <Box sx={{ mt: 0.75, pt: 0.6, borderTop: `1px solid ${TEXT_GRAY}` }}>
                  <Box sx={{ width: '50%', borderBottom: `1px solid ${TEXT_GRAY}`, height: 8, mb: 0.15 }} />
                  <Typography sx={{ fontSize: '5.6pt', color: TEXT_GRAY, fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.2 }}>
                    Authorized Signature
                  </Typography>
                </Box>
              </Box>
            </Paper>
            </Box>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
          Use &quot;Show back&quot; to see card reverse. Physical card may be printed externally.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/member-portal')}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
